import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useLocation } from 'react-router'

import { ArrowBackIcon } from '@toasttab/buffet-pui-icons'
import {
  EditableComponentName,
  FieldType,
  ModuleConfig,
  ModuleField,
  useEditor
} from '@toasttab/sites-components'
import classnames from 'classnames'
import debounce from 'lodash/debounce'

import { Image as ImageObject, LogoSizeOption } from 'src/apollo/sites'
import useOnScroll from 'src/lib/js/hooks/useOnScroll'
import { ScrollDirection } from 'src/lib/js/hooks/useScrollDirection'
import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import { ScreenWidth, useIsMobile } from 'src/shared/js/utils/WindowContext'
import { t } from 'src/translations'

import { useFlags } from 'shared/components/common/feature_flags/useFlags'
import Link from 'shared/components/common/link'
import { getLinkEnumsFromSiteData } from 'shared/components/common/link/util'
import GlobalNav, { NavType } from 'shared/components/common/nav/Nav'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useRestaurantRoutes } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'
import {
  EditablePrimaryCTA,
  PrimaryCTAClasses
} from 'shared/components/primary_cta/PrimaryCTA'
import useIsClient from 'shared/js/hooks/useIsClient'
import { SiteData } from 'shared/js/types'

import {
  AdditionalCTAData,
  CTA,
  CTAData
} from 'public/components/default_template/ctas'
import CTAItem from 'public/components/default_template/ctas/CTAItem'
import { getActionBannerContent } from 'public/components/default_template/footer/action_banner_sticky_footer/ActionBannerStickyFooter'
import { getSiteStyles } from 'public/components/default_template/meta/StyleMeta'
import { useWebsiteLayoutSpacing } from 'public/components/default_template/useWebsiteLayoutSpacing'
import CartModal, {
  CartModalIconButton
} from 'public/components/default_template/online_ordering/cart/CartModal'
import { getCheckoutEligibleItems } from 'public/components/default_template/online_ordering/cart/cartUtils'
import { useOptionalCart } from 'src/public/components/online_ordering/CartContext'
import ConvoOrderingModal from 'public/components/default_template/online_ordering/convo_ordering/ConvoOrderingModal'
import SpotlightBanner, {
  getConsolidatedBanners,
  showSpotlight
} from 'public/components/default_template/spotlight/SpotlightBanner'
import { useSpotlightContext } from 'public/components/default_template/spotlight/SpotlightContext'
import { useCurrentRootPath } from 'public/components/default_template/spotlight/useCurrentRootPath'
import { LocaleSelector } from 'public/components/locale_selector'
import { getLocationPickerPath } from 'public/components/pages/location_selection_page/locationSelectionUtils'
import { useShouldShowCartInNavigation } from 'public/components/pages/WrapWithCartRelatedProviders'

import { ResponsiveNavItems } from './NavItems'
import NavLogo from './NavLogo'
import UserNav from './UserNav'
import { getNavColorStyles, getNavOpacity } from './navUtils'
import AuthTooltip from '../online_ordering/account/authTooltip/AuthTooltip'

export type CommonNavProps = {
  shouldShowPreviewBanner: boolean
  cartDisabled?: boolean
  className?: string
  logoSize?: LogoSizeOption | null
  logoSrc: string
  logoObject?: ImageObject | null
  navType?: NavType | null
  nonPrimaryCtas?: CTAData[] | null
  withCart?: boolean
  withOOOptions?: boolean
  withShadow?: boolean
  // certain pages (online ordering) should always display toast login
  forceDisplayToastLogin?: boolean
  // flag to force display of auth tooltip on non-OO pages (i.e. location picker page)
  showAuthTooltip?: boolean
}

type NavContentProps = CommonNavProps & {
  restaurant?: SiteData
  scrollDirection?: ScrollDirection
  ctaProps?: AdditionalCTAData
  primaryCtaContent?: CTA | null
  setDisplayingOverlay?: (d: boolean) => void
  logoLink?: string
  logoLinkSameTab?: boolean
}

type WrappedNavContentProps = NavContentProps & {
  route: string
}

type PrimaryCTAFactory = ({
  excludeCart
}: {
  excludeCart?: boolean
}) => React.JSX.Element | null

export type NavSchemaFlags = {
  shouldShowCartInNavigation: boolean
}

export const useNavSchema = (
  restaurant: SiteData | undefined,
  { shouldShowCartInNavigation = false }: NavSchemaFlags
) => {
  const { track } = useTracker()

  return useMemo(() => {
    const style = getNavColorStyles(restaurant, true)
    const navConfig = restaurant?.content?.navConfig
    const navLayout = navConfig?.navLayout || 'header-layout-1'
    const themeEnabled = restaurant?.theme?.enabled
    const [
      bgColorPath,
      textColorPath,
      actionBannerBgColor,
      actionBannerButtonBgColor,
      actionBannerButtonTextColor
    ] = themeEnabled
      ? [
          'theme.colorOverrides.navigation.background',
          'theme.colorOverrides.navigation.text',
          'theme.colorOverrides.actionBannerColorOverrides.background',
          'theme.colorOverrides.actionBannerColorOverrides.ctaBackground',
          'theme.colorOverrides.actionBannerColorOverrides.ctaText'
        ]
      : [
          'content.navConfig.backgroundColor',
          'content.navConfig.textColor',
          'meta.legacyTheme.colorOverrides.actionBannerColorOverrides.background',
          'meta.legacyTheme.colorOverrides.actionBannerColorOverrides.ctaBackground',
          'meta.legacyTheme.colorOverrides.actionBannerColorOverrides.ctaText'
        ]

    const { background, textContrastingColor, primaryColor } = getSiteStyles(
      restaurant?.meta ?? {}
    )
    const linkEnums = restaurant
      ? getLinkEnumsFromSiteData(restaurant)
      : undefined

    const [actionBannerLink, actionBannerText] = getActionBannerContent(
      navConfig?.actionBannerConfig,
      linkEnums
    )

    const navSchema = {
      name: 'navigation bar' as EditableComponentName,
      displayName: 'Nav Bar',
      actions: [],
      schema: {
        fields: [
          {
            displayName: 'Background color',
            path: bgColorPath,
            type: FieldType.BackgroundColor,
            value: style.backgroundColor
          },
          {
            displayName: 'Text color',
            path: textColorPath,
            type: FieldType.Color,
            value: style.color
          },
          {
            displayName: 'Opacity',
            path: 'content.navConfig.opacity',
            type: FieldType.Slider,
            value: navConfig?.opacity
          },
          {
            displayName: 'Overlaid',
            path: 'content.navConfig.overlaid',
            type: FieldType.Boolean,
            value: navConfig?.overlaid
          },
          {
            displayName: 'Sticky',
            path: 'content.navConfig.sticky',
            type: FieldType.Boolean,
            value: navConfig?.sticky
          },
          {
            displayName: 'Primary CTA',
            path: 'content.primaryCta',
            type: FieldType.Boolean,
            value: !!restaurant?.content?.primaryCta,
            activeValue: {
              nav: true,
              text: 'View Menu',
              type: 'menu',
              link: '/menu',
              submenus: []
            }
          },
          // the alwaysShowPrimaryCTA should only show if it is a hamburger nav layout and if primaryCTA is on
          ...(!!restaurant?.content?.primaryCta &&
          isHamburgerAlwaysVisibleInLayout(navLayout)
            ? [
                {
                  displayName: 'Always Show Primary CTA',
                  path: 'content.navConfig.alwaysShowPrimaryCTA',
                  type: FieldType.Boolean,
                  value:
                    navConfig?.alwaysShowPrimaryCTA &&
                    !!restaurant?.content?.primaryCta
                } as ModuleField
              ]
            : []),
          {
            displayName: 'Show Toast Login',
            path: 'content.navConfig.showToastLogin',
            type: FieldType.Boolean,
            value: navConfig?.showToastLogin ?? true
          },
          {
            displayName: 'Open Links in Same Tab',
            path: 'content.navConfig.openExternalLinksInSameTab',
            type: FieldType.Boolean,
            value: navConfig?.openExternalLinksInSameTab,
            helperText:
              'This setting will enable linking to external sites to happen in the same tab the user is browsing in.'
          }
        ]
      } as ModuleConfig
    }

    if (shouldShowCartInNavigation) {
      navSchema.schema.fields.push({
        displayName: 'Show Cart',
        path: 'content.navConfig.showCartInNavigation',
        type: FieldType.Boolean,
        value: navConfig?.showCartInNavigation
      } as ModuleField)
    }

    navSchema.schema.fields.push({
      type: FieldType.GroupedFields,
      group: [
        {
          displayName: 'Enable Action Banner',
          path: 'content.navConfig.actionBannerConfig.enabled',
          type: FieldType.Boolean,
          value: navConfig?.actionBannerConfig?.enabled ?? true,
          helperText:
            'The action banner ensures that a button linking to a specific page is always visible when viewing your site on a mobile device.',
          onClick: (e: any) => {
            track(TrackingEvents.ACTION_BANNER_ADMIN, {
              visibility: e.target.checked ? 'enabled' : 'disabled',
              ctaLink: actionBannerLink,
              ctaText: actionBannerText
            })
          }
        },
        {
          displayName: 'Action Banner Link',
          path: 'content.navConfig.actionBannerConfig.ctaLink',
          type: FieldType.EnumOrOther,
          value: actionBannerLink,
          enums: linkEnums,
          hidden: !navConfig?.actionBannerConfig?.enabled
        },
        {
          displayName: 'Action Banner Text',
          path: 'content.navConfig.actionBannerConfig.ctaText',
          type: FieldType.Text,
          value: actionBannerText,
          defaultValue: actionBannerText,
          validation: { required: true },
          hidden: !navConfig?.actionBannerConfig?.enabled
        },
        {
          displayName: 'Action Banner Background Color',
          type: FieldType.Color,
          path: actionBannerBgColor,
          defaultValue: themeEnabled
            ? restaurant?.theme?.colorScheme?.surface?.secondary
            : background,
          hidden: !navConfig?.actionBannerConfig?.enabled,
          showClear: true
        },
        {
          displayName: 'Action Button Background Color',
          type: FieldType.Color,
          path: actionBannerButtonBgColor,
          defaultValue: themeEnabled
            ? restaurant?.theme?.colorScheme?.background?.primary
            : primaryColor,
          hidden: !navConfig?.actionBannerConfig?.enabled,
          showClear: true
        },
        {
          displayName: 'Action Button Text Color',
          type: FieldType.Color,
          path: actionBannerButtonTextColor,
          defaultValue: themeEnabled
            ? restaurant?.theme?.colorScheme?.text?.onBgPrimary
            : textContrastingColor,
          hidden: !navConfig?.actionBannerConfig?.enabled,
          showClear: true
        }
      ]
    } as ModuleField)

    return navSchema
  }, [restaurant, shouldShowCartInNavigation, track])
}

const WrappedNavContent = (
  props: React.PropsWithChildren<WrappedNavContentProps>
) => {
  const { useEditableRef, isEditor } = useEditor()
  // After the logo loads, rerender the parent of ResponsiveNavItems
  // so that ResponsiveNavItems will resize to fit the screen
  const [logoLoaded, setLogoLoaded] = useState(false)
  const onLogoLoad = useCallback(() => setLogoLoaded(true), [])
  const [scrolledToTop, setScrolledToTop] = useState(true)
  const { isHidden } = useSpotlightContext()
  const {
    toastProduct,
    ooRestaurant,
    currentPageType,
    restaurant,
    restaurant: {
      config: { isOnlineOrderingEnabled }
    }
  } = useRestaurant()
  const { ooConvoOrderingEnabled, ooLocaleSelector } = useFlags()
  const isMobile = useIsMobile(ScreenWidth.SMALL)
  const isClient = useIsClient()
  const websiteLayoutSpacing = useWebsiteLayoutSpacing()
  const { orderPath, checkoutPath, ecommPath, ecommCheckoutPath } =
    useRestaurantRoutes()
  const cartContext = useOptionalCart()
  const cart = cartContext?.cart
  // Ask Maps builds a cart then directs the guest to the checkout page. If the guest
  // wants to add more items, they should return to Ask Maps.
  const isAskMapsCart = cartContext?.cartReferrer === 'ask_maps'
  const hasCartItems = !!getCheckoutEligibleItems(cart)?.length

  const isEcommCheckoutPage = props.route === ecommCheckoutPath
  const isCheckoutPageVariant = [checkoutPath, ecommCheckoutPath].includes(
    props.route
  )

  // Multi-location logic for back button redirect
  const locationPickerPath = getLocationPickerPath(
    ooRestaurant?.managementSetGuid || restaurant?.externalId,
    restaurant?.externalRef
  )
  const isMultiLocation =
    (restaurant.locations?.length ?? 0) > 1 && !!locationPickerPath

  const shouldShowCartInNavigation =
    (useShouldShowCartInNavigation(currentPageType) || isCheckoutPageVariant) &&
    !isAskMapsCart

  // Don't show cart on checkout pages when the cart is empty
  const hideCartOnEmptyCheckout = isCheckoutPageVariant && !hasCartItems

  let style = getNavColorStyles(props.restaurant, true)
  const hamburgerStyle = { fill: style.color }
  const navConfig = props.restaurant?.content?.navConfig
  let navType = props.navType
  if (!navType) {
    if (navConfig?.overlaid) {
      if (navConfig?.sticky === false) {
        navType = 'absoluteNav'
      } else {
        navType = 'fixedNav'
      }
    } else if (navConfig?.sticky === false) {
      navType = 'normal'
    } else {
      navType = 'stickyNav'
    }
  }
  const navLayout = !isCheckoutPageVariant
    ? navConfig?.navLayout || 'header-layout-1'
    : 'header-layout-checkout'

  const scrollReponsiveNav = navType === 'fixedNav' || navType === 'stickyNav'
  if (scrollReponsiveNav && !scrolledToTop && getNavOpacity(navConfig) === 0) {
    const stylesWithoutOpacity = getNavColorStyles(props.restaurant, false)
    style.backgroundColor = stylesWithoutOpacity.backgroundColor
  }

  const toggleMenu = useCallback(() => {
    // toggle the menu
    props.setDisplayingOverlay!(true)
  }, [props.setDisplayingOverlay])

  // to make the hamburger more accessible for keyboard access
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'Enter') {
        e.preventDefault() // Prevent the default space key action (page scroll)
        toggleMenu()
      }
    },
    [toggleMenu]
  )

  const debouncedScrollUpdate = debounce(
    () => setScrolledToTop(window.scrollY === 0),
    25
  )
  useOnScroll(() => debouncedScrollUpdate())

  const navSchema = useNavSchema(props.restaurant, {
    shouldShowCartInNavigation
  })
  const { editableRef: editableNavBarRef, pushSchema } =
    useEditableRef<HTMLDivElement>(navSchema)
  useEffect(() => {
    // resubmit the changed schema when primaryCTA toggle changes.
    pushSchema()
  }, [pushSchema, props.restaurant?.content?.primaryCta])
  const { editableRef: editableNavListRef } = useEditableRef<HTMLDivElement>({
    name: 'navigation list',
    displayName: 'Nav List',
    actions: [],
    schema: { fields: [] }
  })
  const { editableRef: editableHamburgerRef } =
    useEditableRef<HTMLButtonElement>({
      name: 'navigation list',
      displayName: 'Nav List',
      actions: [],
      schema: { fields: [] }
    })

  const currentRootPath = useCurrentRootPath(props.route)
  const showFixedTopSpotlight = showSpotlight(
    { fixed: true },
    props?.restaurant?.content?.spotlightBanner,
    ooRestaurant?.spotlightConfig,
    getConsolidatedBanners(
      props.restaurant?.content?.spotlightBanner,
      ooRestaurant?.spotlightConfig
    ),
    currentRootPath,
    toastProduct
  )

  const getBackButtonHref = () => {
    if (isEcommCheckoutPage) {
      return ecommPath
    }
    // OO checkout: multi-location with empty cart goes to location picker, otherwise order page
    return isMultiLocation && !hasCartItems ? locationPickerPath : orderPath
  }

  const getBackButtonLabel = () => {
    if (isEcommCheckoutPage) {
      return t('nav-content.back-to-store')
    }
    if (isMultiLocation && !hasCartItems) {
      return t('nav-content.back-to-ordering')
    }
    return t('nav-content.back-to-menu')
  }

  const backButton =
    isCheckoutPageVariant && !isAskMapsCart ? (
      <Link
        icon={<ArrowBackIcon accessibility='decorative' />}
        href={getBackButtonHref()}
        className='checkoutBackButton'
        sameTab
        ariaLabel={getBackButtonLabel()}
      >
        {!isMobile && getBackButtonLabel()}
      </Link>
    ) : null

  const hasHamburger = !!props.setDisplayingOverlay
  const hamburger = hasHamburger ? (
    <li
      className={classnames('hamburgerWrapper', {
        'hidden-md-up': !isHamburgerAlwaysVisibleInLayout(navLayout)
      })}
    >
      <button
        className={classnames('hamburger', {
          'hidden-md-up': !isHamburgerAlwaysVisibleInLayout(navLayout)
        })}
        onClick={() => toggleMenu()}
        onKeyDown={handleKeyDown}
        nav-role='hamburger-button'
        data-testid='hamburger-button'
        tabIndex={0}
        aria-label={'hamburger menu'}
        ref={editableHamburgerRef}
      >
        <svg viewBox='0 0 100 80' width='20' height='20' {...hamburgerStyle}>
          <rect width='100' height='15'></rect>
          <rect y='30' width='100' height='15'></rect>
          <rect y='60' width='100' height='15'></rect>
        </svg>
      </button>
    </li>
  ) : null

  // Determine which layouts need horizontal gaps for centered logos
  const shouldAddLogoGap = [
    'header-layout-4',
    'header-layout-5',
    'header-layout-6',
    'header-layout-7',
    'header-layout-8',
    'header-layout-checkout'
  ].includes(navLayout)

  const logo = (
    <li
      nav-role='logo'
      className={classnames({
        'logo-with-horizontal-gap': shouldAddLogoGap
      })}
    >
      <NavLogo
        logoLink={isCheckoutPageVariant ? null : props.logoLink}
        logoLinkSameTab={props.logoLinkSameTab}
        style={style}
        onLoad={onLogoLoad}
        logoSrc={props.logoSrc}
        logoObject={props.logoObject}
        logoSize={props.logoSize || navConfig?.logoSize}
        scrollDirection={scrollReponsiveNav ? props.scrollDirection : undefined}
      />
    </li>
  )
  const links = props.ctaProps ? (
    <>
      <li
        role='none'
        className={classnames('hidden-sm-down', {
          center: props.withOOOptions
        })}
        nav-role='links'
      >
        <div
          role='none'
          className='navLinks'
          ref={editableNavListRef}
          key={`nav-links-${logoLoaded}`}
        >
          <ResponsiveNavItems
            ctas={props.nonPrimaryCtas}
            ctaProps={props.ctaProps}
            parentRef={editableNavListRef}
            moreMenuLabel={navConfig?.moreMenuLabel}
            color={navConfig?.textColor}
          />
        </div>
      </li>
    </>
  ) : null
  // Only show cart in navigation if the flag is on, the navConfig allows it, and online ordering is enabled
  const canShowCartInNavigation =
    shouldShowCartInNavigation &&
    navConfig?.showCartInNavigation &&
    isOnlineOrderingEnabled

  const primaryCTA: PrimaryCTAFactory = useCallback(
    ({ excludeCart = false }) => {
      // Don't show cart if excludeCart is true (for layouts that always show primary CTA)
      const showCartModal =
        !excludeCart &&
        (props.withCart || canShowCartInNavigation) &&
        !hideCartOnEmptyCheckout
      return (
        <ul role='none' nav-role='primary-cta'>
          {isClient && ooConvoOrderingEnabled && !isEditor && showCartModal ? (
            <li>
              <ConvoOrderingModal />
            </li>
          ) : null}
          {isEditor && showCartModal ? (
            <li>
              <CartModalIconButton />
            </li>
          ) : null}
          {!isEditor && showCartModal ? (
            <li>
              <CartModal
                disabled={props.cartDisabled}
                isOrderPage={props.withOOOptions}
              />
            </li>
          ) : null}
          {props.primaryCtaContent ? (
            <li className='cta hidden-sm-down'>
              <CTAItem
                cta={props.primaryCtaContent}
                isPrimary
                className={PrimaryCTAClasses}
                editPath='content.primaryCta'
              >
                <EditablePrimaryCTA
                  editPath='content.primaryCta'
                  href={props.primaryCtaContent?.link}
                  text={props.primaryCtaContent?.text}
                  type={props.primaryCtaContent.type}
                  sameTab={!!navConfig?.openExternalLinksInSameTab}
                />
              </CTAItem>
            </li>
          ) : null}
          {isClient && ooLocaleSelector && (
            <li className={classnames(hasHamburger ? 'hidden-sm-down' : '')}>
              <LocaleSelector />
            </li>
          )}
          <li>
            <UserNav forceDisplayToastLogin={props.forceDisplayToastLogin} />
          </li>
        </ul>
      )
    },
    [
      props.cartDisabled,
      props.primaryCtaContent,
      props.withCart,
      props.forceDisplayToastLogin,
      isEditor,
      canShowCartInNavigation,
      props.withOOOptions,
      ooConvoOrderingEnabled,
      ooLocaleSelector,
      hasHamburger,
      hideCartOnEmptyCheckout,
      isClient,
      navConfig?.openExternalLinksInSameTab
    ]
  )

  const ooOptions = useMemo(() => {
    const shouldShowCart =
      props.withOOOptions || props.withCart || canShowCartInNavigation
    if (!shouldShowCart || hideCartOnEmptyCheckout) return null

    return (
      <ul nav-role='primary-cta'>
        <li>
          {isEditor ? (
            <CartModalIconButton />
          ) : (
            <CartModal
              isOrderPage={props.withOOOptions}
              disabled={props.cartDisabled}
            />
          )}
        </li>
      </ul>
    )
  }, [
    props.withOOOptions,
    props.withCart,
    canShowCartInNavigation,
    isEditor,
    props.cartDisabled,
    hideCartOnEmptyCheckout
  ])

  const alwaysPrimaryCTA: PrimaryCTAFactory = useCallback(
    ({ excludeCart = false }) =>
      navConfig?.alwaysShowPrimaryCTA &&
      !!props.restaurant?.content?.primaryCta ? (
        <div data-testid='alwaysShowPrimaryCta'>
          {primaryCTA({ excludeCart })}
        </div>
      ) : null,
    [
      navConfig?.alwaysShowPrimaryCTA,
      primaryCTA,
      props.restaurant?.content?.primaryCta
    ]
  )
  const navChildren = organizeNavChildren(
    {
      hamburger,
      logo,
      links,
      primaryCTA,
      ooOptions,
      alwaysPrimaryCTA,
      backButton
    },
    navLayout
  )
  return (
    <>
      {navType === 'stickyNav' && <SpotlightBanner fixed={false} />}
      {!scrollReponsiveNav && <SpotlightBanner fixed={true} />}
      <GlobalNav
        navType={navType}
        shouldShowPreviewBanner={props.shouldShowPreviewBanner}
        className={classnames(props.className, {
          spotlightOffset: showFixedTopSpotlight && !isHidden
        })}
      >
        {navType !== 'stickyNav' && (
          <SpotlightBanner
            fixed={false}
            style={
              navType === 'fixedNav' && !scrolledToTop ? { height: 0 } : {}
            }
          />
        )}
        {scrollReponsiveNav && <SpotlightBanner fixed={true} />}
        <div
          className={classnames('navWrapper', { withShadow: props.withShadow })}
          style={{ ...style, ...websiteLayoutSpacing }}
          ref={editableNavBarRef}
        >
          <div
            className={classnames('nav', navLayout)}
            data-testid={`nav-${navLayout}`}
          >
            {navChildren}
          </div>
        </div>
        {props.children}
        {(props.withOOOptions || props.showAuthTooltip) && <AuthTooltip />}
      </GlobalNav>
    </>
  )
}

const EditorNavContent = (props: NavContentProps) => {
  const { url } = useEditor()
  return <WrappedNavContent {...props} route={url} />
}

const SitesNavContent = (props: NavContentProps) => {
  const { pathname } = useLocation()
  return <WrappedNavContent {...props} route={pathname} />
}

const NavContent = (props: NavContentProps) => {
  const { isEditor } = useEditor()
  return isEditor ? (
    <EditorNavContent {...props} />
  ) : (
    <SitesNavContent {...props} />
  )
}

export const isHamburgerAlwaysVisibleInLayout = (layout: string): boolean => {
  switch (layout) {
    case 'header-layout-3':
    case 'header-layout-6':
    case 'header-layout-7':
      return true
    default:
      return false
  }
}

type NavChildrenType = {
  logo: ReactNode
  hamburger: ReactNode
  primaryCTA: PrimaryCTAFactory
  links: ReactNode
  ooOptions: ReactNode
  alwaysPrimaryCTA: PrimaryCTAFactory
  backButton?: ReactNode
}

export const organizeNavChildren = (
  {
    hamburger,
    logo,
    links,
    primaryCTA,
    ooOptions,
    alwaysPrimaryCTA,
    backButton
  }: NavChildrenType,
  layout: string
): ReactNode => {
  switch (layout) {
    case 'header-layout-checkout':
      return (
        <>
          <div role='none' className='left'>
            {backButton}
          </div>
          <ul className='middle logo-container'>{logo}</ul>
          <div role='none' className='right'>
            {ooOptions}
          </div>
        </>
      )
    case 'header-layout-8':
      return (
        <>
          <ul className='left'>{hamburger}</ul>
          <ul className='middle'>
            {logo}
            <li>
              <div id='navigation list' />
            </li>
            {links}
          </ul>
          <div role='none' className='right'>
            {primaryCTA({})}
          </div>
        </>
      )
    case 'header-layout-7':
      return (
        <>
          <div role='none' className='left'>
            {' '}
          </div>
          <ul className='middle logo-container'>{logo}</ul>
          <div role='none' className='right'>
            {ooOptions}
            <ul>
              <li>{alwaysPrimaryCTA({ excludeCart: true })}</li>
              <li>
                <div id='navigation list' />
              </li>
              {hamburger}
            </ul>
          </div>
        </>
      )
    case 'header-layout-6':
      return (
        <>
          <ul className='left'>
            {hamburger}
            <li>
              <div id='navigation list' />
            </li>
            <li>{alwaysPrimaryCTA({ excludeCart: true })}</li>
          </ul>
          <ul className='middle logo-container'>{logo}</ul>
          <div role='none' className='right'>
            {ooOptions}
          </div>
        </>
      )
    case 'header-layout-5':
      return (
        <>
          <div role='none' className='left'>
            {' '}
          </div>
          <ul className='middle logo-container'>{logo}</ul>
          <div role='none' className='right'>
            <ul>{links}</ul>
            {primaryCTA({})}
            <ul>{hamburger}</ul>
            <div id='navigation list' />
          </div>
        </>
      )
    case 'header-layout-4':
      return (
        <>
          <ul className='left'>
            {hamburger}
            {links}
          </ul>
          <div id='navigation list' />
          <ul className='middle logo-container'>{logo}</ul>
          <div role='none' className='right'>
            {primaryCTA({})}
          </div>
        </>
      )
    case 'header-layout-3':
      return (
        <>
          <ul className='left logo-container'>{logo}</ul>
          <div role='none' className='middle'></div>
          <div role='none' className='right'>
            {ooOptions}
            {alwaysPrimaryCTA({ excludeCart: true })}
            <ul>{hamburger}</ul>
          </div>
          <div id='navigation list' />
        </>
      )
    case 'header-layout-2':
    case 'header-layout-1':
    default:
      return (
        <>
          <ul className='left logo-container'>
            {hamburger}
            {logo}
          </ul>
          <ul className='middle'>{links}</ul>
          <div id='navigation list' />
          <div role='none' className='right'>
            {primaryCTA({})}
          </div>
        </>
      )
  }
}

export default NavContent
